import React, { useState, useEffect } from 'react';
import {
    Box, Button, Card, CardContent, CardMedia, Grid, TextField,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, CircularProgress, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Alert, Tooltip, TablePagination
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import Swal from 'sweetalert2';
import { useAccesos } from '../contexts/AccesosContext';

function AdminProductoImagen() {
    const { tienePermiso } = useAccesos();
    const server = process.env.REACT_APP_API_ROUTE;

    // State para lista de productos
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // State para upload
    const [selectedFile, setSelectedFile] = useState(null);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [selectedProductoId, setSelectedProductoId] = useState(null);

    // State para preview
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState('');
    const [previewProducto, setPreviewProducto] = useState('');

    // Cargar productos con clasificación por uso = 3
    const fetchProductos = async (searchTerm = '') => {
        setLoading(true);
        try {
            let url = `${server}/IvProductoes?idClasificacionPorUso=3`;
            if (searchTerm) {
                url += `&search=${encodeURIComponent(searchTerm)}`;
            }
            
            const response = await fetch(url);
            if (!response.ok) throw new Error('Error loading productos');
            
            const data = await response.json();
            // Filter to only show those with clasificacion por uso = 3
            const filtered = (data.$values || data || []).filter(p => p.idClasificacionPorUso === 3);
            setProductos(filtered);
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', 'Error al cargar productos', 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProductos();
    }, []);

    const handleSearch = () => {
        setPage(0);
        fetchProductos(busqueda);
    };

    const handleFileSelect = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validar tipo de archivo
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            if (!allowedTypes.includes(file.type)) {
                Swal.fire('Error', 'Solo se permiten archivos: JPG, PNG, GIF, WebP', 'error');
                return;
            }
            // Validar tamaño (5MB máximo)
            if (file.size > 5 * 1024 * 1024) {
                Swal.fire('Error', 'El archivo no debe exceder 5MB', 'error');
                return;
            }
            setSelectedFile(file);
        }
    };

    const handleUploadImage = async (productoId) => {
        if (!selectedFile) {
            Swal.fire('Validación', 'Seleccione un archivo de imagen', 'warning');
            return;
        }

        if (!tienePermiso('Administrar Imagenes Productos')) {
            Swal.fire('Permiso', 'No tiene permiso para subir imágenes', 'error');
            return;
        }

        setUploadLoading(true);
        const formData = new FormData();
        formData.append('file', selectedFile);

        try {
            const response = await fetch(`${server}/IvProductoes/${productoId}/imagen`, {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const result = await response.json();
                Swal.fire('Éxito', 'Imagen cargada exitosamente', 'success');
                setSelectedFile(null);
                setSelectedProductoId(null);
                // Recargar productos
                fetchProductos(busqueda);
            } else {
                const error = await response.json();
                Swal.fire('Error', error.message || 'Error al cargar imagen', 'error');
            }
        } catch (error) {
            console.error('Error:', error);
            Swal.fire('Error', error.message, 'error');
        } finally {
            setUploadLoading(false);
        }
    };

    const handleDeleteImage = async (productoId, productoNombre) => {
        const result = await Swal.fire({
            title: '¿Eliminar imagen?',
            text: `¿Está seguro de que desea eliminar la imagen de ${productoNombre}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (!result.isConfirmed) return;

        try {
            const response = await fetch(`${server}/IvProductoes/${productoId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ imagenUrl: null })
            });

            if (response.ok) {
                Swal.fire('Éxito', 'Imagen eliminada exitosamente', 'success');
                fetchProductos(busqueda);
            } else {
                Swal.fire('Error', 'Error al eliminar imagen', 'error');
            }
        } catch (error) {
            Swal.fire('Error', error.message, 'error');
        }
    };

    const handlePreview = (imagenUrl, nombreProducto) => {
        setPreviewUrl(imagenUrl);
        setPreviewProducto(nombreProducto);
        setPreviewOpen(true);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const paginatedProductos = productos.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    return (
        <div className="right_col" role="main">
            <div className="x_content">
                <div className="x_title">
                    <h2>Administración de Imágenes — Productos (Clasificación por Uso = Imagenes)</h2>
                </div>

                {!tienePermiso('Administrar Imagenes Productos') && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        ⚠️ Usted no tiene permiso para administrar imágenes de productos
                    </Alert>
                )}

                {/* Búsqueda */}
                <Box sx={{ mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <TextField
                                fullWidth
                                label="Buscar producto (código, descripción)"
                                value={busqueda}
                                onChange={(e) => setBusqueda(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button
                                fullWidth
                                variant="contained"
                                onClick={handleSearch}
                                disabled={loading}
                            >
                                {loading ? 'Buscando...' : 'Buscar'}
                            </Button>
                        </Grid>
                    </Grid>
                </Box>

                {/* Tabla de productos */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <TableContainer component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                        <TableCell><strong>Código</strong></TableCell>
                                        <TableCell><strong>Descripción</strong></TableCell>
                                        <TableCell align="center"><strong>Imagen</strong></TableCell>
                                        <TableCell align="center"><strong>Estado</strong></TableCell>
                                        <TableCell align="center"><strong>Acciones</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {paginatedProductos.length > 0 ? (
                                        paginatedProductos.map((producto) => (
                                            <TableRow key={producto.id} hover>
                                                <TableCell>{producto.codigo || '—'}</TableCell>
                                                <TableCell>{producto.descripcion || '—'}</TableCell>
                                                <TableCell align="center">
                                                    {producto.imagenUrl ? (
                                                        <Box
                                                            component="img"
                                                            src={producto.imagenUrl}
                                                            alt={producto.descripcion}
                                                            sx={{
                                                                maxWidth: '60px',
                                                                maxHeight: '60px',
                                                                borderRadius: 1,
                                                                cursor: 'pointer',
                                                                '&:hover': { opacity: 0.8 }
                                                            }}
                                                            onClick={() => handlePreview(producto.imagenUrl, producto.descripcion)}
                                                        />
                                                    ) : (
                                                        <Chip label="Sin imagen" size="small" variant="outlined" />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center">
                                                    {producto.imagenUrl ? (
                                                        <Chip label="✓ Cargada" size="small" color="success" variant="outlined" />
                                                    ) : (
                                                        <Chip label="No cargada" size="small" color="error" variant="outlined" />
                                                    )}
                                                </TableCell>
                                                <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title="Subir/Actualizar imagen">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => setSelectedProductoId(producto.id)}
                                                                disabled={!tienePermiso('Administrar Imagenes Productos')}
                                                            >
                                                                <CloudUploadIcon fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    {producto.imagenUrl && (
                                                        <>
                                                            <Tooltip title="Vista previa">
                                                                <IconButton
                                                                    size="small"
                                                                    color="info"
                                                                    onClick={() => handlePreview(producto.imagenUrl, producto.descripcion)}
                                                                >
                                                                    <PreviewIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="Eliminar imagen">
                                                                <span>
                                                                    <IconButton
                                                                        size="small"
                                                                        color="error"
                                                                        onClick={() => handleDeleteImage(producto.id, producto.descripcion)}
                                                                        disabled={!tienePermiso('Administrar Imagenes Productos')}
                                                                    >
                                                                        <DeleteIcon fontSize="small" />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#999' }}>
                                                No se encontraron productos
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25]}
                            component="div"
                            count={productos.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </>
                )}

                {/* Dialog para subir imagen */}
                <Dialog
                    open={selectedProductoId !== null}
                    onClose={() => {
                        setSelectedProductoId(null);
                        setSelectedFile(null);
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>Subir Imagen del Producto</DialogTitle>
                    <DialogContent>
                        <Box sx={{ py: 2 }}>
                            <TextField
                                type="file"
                                inputProps={{ accept: 'image/*' }}
                                onChange={handleFileSelect}
                                fullWidth
                                sx={{ mb: 2 }}
                            />
                            {selectedFile && (
                                <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2 }}>
                                    <Box component="img"
                                        src={URL.createObjectURL(selectedFile)}
                                        alt="preview"
                                        sx={{ maxWidth: '100%', maxHeight: '300px' }}
                                    />
                                </Box>
                            )}
                            <Alert severity="info">
                                📝 Formatos permitidos: JPG, PNG, GIF, WebP | Tamaño máximo: 5MB
                            </Alert>
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setSelectedProductoId(null);
                                setSelectedFile(null);
                            }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            onClick={() => handleUploadImage(selectedProductoId)}
                            variant="contained"
                            disabled={uploadLoading || !selectedFile}
                        >
                            {uploadLoading ? <CircularProgress size={20} /> : 'Subir'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog para preview */}
                <Dialog
                    open={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>{previewProducto}</DialogTitle>
                    <DialogContent>
                        <Box
                            component="img"
                            src={previewUrl}
                            alt={previewProducto}
                            sx={{ width: '100%', mt: 2 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPreviewOpen(false)}>Cerrar</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
}

export default AdminProductoImagen;
